/**
 * Provisioning Browser Dispatch
 *
 * A dispatch is one hand-off of a batch of browser steps to an automation
 * runner. The backend spawns the Journey CLI locally — that is the ONLY target
 * while browser dispatch is being proven out on a dev host; the GitHub Actions
 * path was deleted rather than left as a dormant enum member. The runner
 * reports per-step progress back through the internal report endpoint, and the
 * dispatch record is what the dashboard polls.
 *
 * Parallelism: the dispatcher groups requested steps by browser-session slug
 * (`apple-developer` vs `google-portal`) — one dispatch per slug, so two
 * runners never race the same trusted-browser session, while distinct portals
 * run concurrently.
 */

import { z } from 'zod'
import { BrowserSessionSlugSchema } from './provisioning-browser-steps'
import { ObservedStandingProvenanceSchema } from './publishing-account'

export const ProvisioningDispatchStatusSchema = z.enum([
  'PENDING',
  'DISPATCHED',
  'RUNNING',
  'SUCCEEDED',
  'PARTIAL',
  'FAILED',
  'TIMED_OUT'
])
export type ProvisioningDispatchStatus = z.infer<typeof ProvisioningDispatchStatusSchema>

/** Per-step outcome as reported by the runner (lowercase: runner vocabulary, not the DB enum). */
export const BrowserStepRunStatusSchema = z.enum(['pending', 'running', 'succeeded', 'failed', 'blocked', 'skipped'])
export type BrowserStepRunStatus = z.infer<typeof BrowserStepRunStatusSchema>

/**
 * The two artifacts a faulted step leaves behind (Track I, U48).
 *
 * A full-page image and the page's serialized DOM, captured at the moment the
 * step's verdict settled. Deliberately closed at two: the pair is what a human
 * needs to diagnose a 3am portal failure without touching a live vendor account
 * again, and — under D8 — is also the whole of the deferred interpreter's input.
 */
export const StepEvidenceKindSchema = z.enum(['screenshot', 'dom'])
export type StepEvidenceKind = z.infer<typeof StepEvidenceKindSchema>

/**
 * Pointers to one step's stored evidence. KEYS ONLY — never the bytes.
 *
 * The artifact itself lives in the evidence prefix in S3 behind a 14-day
 * lifecycle rule, and is handed out only as a short-lived presigned URL to a
 * super-admin. Putting the bytes here (base64 in the report body) would copy a
 * customer's portal page into `ScheduledTaskExecution.result` and into every log
 * line that echoes it, which is exactly the holding the capture rules exist to
 * bound.
 *
 * `provenance` is D5's vocabulary applied to the artifact: a screenshot of a
 * vendor portal IS a scrape, and recording that from the first capture is what
 * lets the interpreter tier's `interpreted` values be separated from these later
 * — under D8 the interpreter reads exactly these artifacts. This is a label on
 * the CAPTURE, not the row-level observed-state label that per-field
 * provenance replaced.
 */
export const StepEvidenceSchema = z.object({
  /** S3 key of the full-page image, absent when that capture failed. */
  screenshotKey: z.string().optional(),
  /** S3 key of the serialized DOM, absent when that capture failed. */
  domKey: z.string().optional(),
  capturedAt: z.string(),
  provenance: ObservedStandingProvenanceSchema.extract(['scraped'])
})
export type StepEvidence = z.infer<typeof StepEvidenceSchema>

export const BrowserStepStateSchema = z.object({
  status: BrowserStepRunStatusSchema,
  /** Runner phase when the state was recorded: probe | apply | commit | read-back. */
  phase: z.string().optional(),
  message: z.string().optional(),
  /** Step output the runner chose to surface (never secret material). */
  output: z.unknown().optional(),
  /**
   * Failure-path evidence (U48). Present only for a step whose verdict was a
   * fault — a succeeded or blocked step carries none, by design.
   */
  evidence: StepEvidenceSchema.optional(),
  updatedAt: z.string()
})
export type BrowserStepState = z.infer<typeof BrowserStepStateSchema>

export const ProvisioningDispatchResponseSchema = z.object({
  id: z.string(),
  /**
   * The owning run, or null for an APP-SCOPED dispatch. Exactly one of `runId` /
   * `mobileAppId` is set: Android store-setup steps (SCH-1936) are fired at one
   * app from the dashboard and belong to no provisioning run.
   */
  runId: z.string().nullable(),
  mobileAppId: z.string().nullable(),
  stepIds: z.array(z.string()),
  sessionSlug: BrowserSessionSlugSchema,
  status: ProvisioningDispatchStatusSchema,
  stepStates: z.record(z.string(), BrowserStepStateSchema),
  error: z
    .object({
      type: z.string(),
      message: z.string()
    })
    .nullable()
    .optional(),
  requestedByUserId: z.string().nullable().optional(),
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string()
})
export type ProvisioningDispatchResponse = z.infer<typeof ProvisioningDispatchResponseSchema>
